import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, DropdownMenu, TableItem } from 'components/atoms';
import LabeledIncomeStatus from 'components/molecules/labeledStatus/LabaleIncomeStatus';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible, useHasPermission } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTranslation } from 'react-i18next';
import {
  PiClockCounterClockwiseBold,
  PiDownload,
  PiHandCoinsBold,
  PiReceiptFill,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { INCOME_TYPE } from 'services/cash-flow/income/types';
import { useGetLabelBalance } from 'services/settlement';
import { BALANCE_TYPE, IFinancesDetail } from 'services/settlement/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrency } from 'utils/global/currency.utils';
import { getMaxFormattedLength } from 'utils/global/getMaxFormattedLength.utils';
import { getNodes } from 'utils/table/nodes.utils';

const BalanceCost = () => {
  const labelId = useParamId('label');
  const hasPermission = useHasPermission();

  usePermissionGuard('SettlementCostPermissionsLIST**');
  usePermissionGuard('SettlementIncomePermissionsLIST**');

  const { t } = useTranslation();
  const navigate = useNavigate();

  const labelBalance = useGetLabelBalance(labelId);

  const tableData = getNodes<IFinancesDetail>(labelBalance.data || []);

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const goCreateCost = () => navigate(`/label/${labelId}/cost/create/file`);

  const goCreateIncost = () => navigate(`/label/${labelId}/income/create/file`);

  const onSelectChange = (action: Action, state: State) => {
    const rowData = tableData.nodes || [];

    const selectedRow = rowData.find(row => row.id === action.payload.id);

    if (selectedRow?.type === 1) {
      navigate(`/label/${labelId}/cost/${selectedRow?.id}/file`);
    } else {
      navigate(`/label/${labelId}/income/${selectedRow?.id}/file`);
    }
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const amountNetLength = getMaxFormattedLength(
    tableData?.nodes ?? [],
    e => e?.amount?.netAmount
  );
  const amountGrossLength = getMaxFormattedLength(
    tableData?.nodes ?? [],
    e => e?.amount?.grossAmount
  );
  const amountToRepaymentLength = getMaxFormattedLength(
    tableData?.nodes ?? [],
    e => e?.amountToRepayment
  );
  const taxLength = getMaxFormattedLength(tableData?.nodes ?? [], e => e?.tax);

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          hideHeader
          topbarContent={
            <>
              <Button onlyIcon isClear onClick={() => navigate('history')}>
                                <Button.Description description={t('history.contract')} />

                                <Button.Icon icon={<PiClockCounterClockwiseBold />} />
              </Button>
              {(hasPermission('SettlementCostPermissionsLIST**', 1) ||
                hasPermission('SettlementIncomePermissionsLIST**', 1)) && (
                <Button
                  variant={BUTTON_VARIANT.submit}
                  onClick={handleComponentVisible}
                  outsideChildren={
                    <DropdownMenu
                      ref={ref}
                      visible={isComponentVisible}
                      style={{ padding: 0 }}
                      justifyPosition='end'>
                      {hasPermission('SettlementCostPermissionsLIST**', 1) && (
                        <Button isClear onClick={goCreateCost} fullWidth>
                          <div
                            style={{
                              alignContent: 'baseline',
                              display: 'flex',
                              width: '100%',
                              gap: '12px',
                            }}>
                            <Button.Icon icon={<PiReceiptFill />} />
                            <Button.Text text={t('label.tabs.cost')} />
                          </div>
                        </Button>
                      )}

                      {hasPermission(
                        'SettlementIncomePermissionsLIST**',
                        1
                      ) && (
                        <Button isClear onClick={goCreateIncost}>
                          <Button.Icon icon={<PiHandCoinsBold />} />
                          <Button.Text text={t('label.tabs.income')} />
                        </Button>
                      )}
                    </DropdownMenu>
                  }>
                  <Button.Text text={t('button.add')} />
                </Button>
              )}
            </>
          }
        />
      }>
      <Table
        id={'LabelBalance'}
        size='compact'
        data={tableData}
        select={select}
        columns={[
          {
            label: t('input.label.name'),
            renderCell: ({ name }) => (
              <TableItem>
                <TableItem.Text text={name} />
              </TableItem>
            ),
            resize: true,
          },

          {
            label: t('input.label.amountToRepayment'),
            renderCell: ({ type, amountToRepayment }) => (
              <TableItem>
                <TableItem.Currency
                  text={
                    type === BALANCE_TYPE.cost
                      ? formatCurrency(amountToRepayment, {
                          length: amountToRepaymentLength,
                        })
                      : undefined
                  }
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.net'),
            renderCell: ({ amount }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amount.netAmount, {
                    length: amountNetLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.gross'),
            renderCell: ({ amount }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amount.grossAmount, {
                    length: amountGrossLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.tax'),
            renderCell: ({ tax }) => (
              <TableItem>
                <TableItem.Currency
                  text={`${formatCurrency(tax, {
                    length: taxLength,
                    suffix: '%',
                  })} `}
                />
              </TableItem>
            ),
            resize: true,
          },

          {
            label: t('input.label.type'),
            renderCell: ({ incomeType, type }) => (
              <TableItem>
                <LabeledIncomeStatus
                  status={
                    type === BALANCE_TYPE.income
                      ? incomeType === INCOME_TYPE.cost
                        ? INCOME_TYPE.basic
                        : incomeType
                      : INCOME_TYPE.cost
                  }
                />
              </TableItem>
            ),
            resize: true,
          },

          {
            label: t('input.label.file'),
            renderCell: ({ fileUrl }) => (
              <TableItem justifyPosition='center'>
                <a
                  href={fileUrl ?? ''}
                  download={fileUrl}
                  aria-disabled={!fileUrl}
                  target='_blank'
                  rel='noreferrer'>
                  <Button type='button' isClear disabled={!fileUrl}>
                    <Button.Icon icon={<PiDownload />} />
                  </Button>
                </a>
              </TableItem>
            ),
            resize: true,
          },
        ]}
      />
    </TopbarPage>
  );
};

export default BalanceCost;
